import React from 'react';
import { View, Image, Pressable, Text, StyleSheet } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Play, Star } from 'lucide-react';
import { Series } from '../types/domain';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../theme/netflixTheme';

interface HeroSectionProps {
  series: Series;
}

export function HeroSection({ series }: HeroSectionProps): JSX.Element {
  const navigate = useNavigate();
  const isComingSoon = series.status === 'ongoing';

  return (
    <View style={styles.hero}>
      {/* Content Left */}
      <View style={styles.contentLeft}>
        <View style={styles.badgesContainer}>
          {isComingSoon && (
            <View style={styles.comingSoonBadge}>
              <Text style={styles.comingSoonBadgeText}>EM BREVE</Text>
            </View>
          )}
          <View style={styles.badge}>
            <Text style={styles.badgeText}>ROMANCE</Text>
          </View>
        </View>

        <Text style={styles.title}>{series.title}</Text>

        <View style={styles.meta}>
          <View style={styles.ratingContainer}>
            <Star size={16} color={NETFLIX_COLORS.accent} fill={NETFLIX_COLORS.accent} />
            <Text style={styles.rating}>{series.rating?.toFixed(1)}</Text>
          </View>
          <Text style={styles.year}>{series.release_year}</Text>
          <Text style={styles.duration}>{series.total_episodes} episódios</Text>
        </View>

        <Text style={styles.description} numberOfLines={4}>
          {series.description}
        </Text>

        <View style={styles.buttons}>
          <Pressable
            style={styles.playBtn}
            onPress={() => navigate(`/watch/${series.id}/1`)}
          >
            <Play size={16} color={NETFLIX_COLORS.white} />
            <Text style={styles.playBtnText}>ASSISTIR</Text>
          </Pressable>

          <Pressable
            style={styles.addBtn}
            onPress={() => navigate(`/series/${series.id}`)}
          >
            <Text style={styles.addBtnText}>+ MINHA LISTA</Text>
          </Pressable>
        </View>
      </View>

      {/* Image Right */}
      <View style={styles.imageContainer}>
        <Image
          source={{ uri: series.poster_url }}
          style={styles.image}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  hero: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: NETFLIX_COLORS.background,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: NETFLIX_SPACING.xl,
    minHeight: 450,
  },
  contentLeft: {
    flex: 1,
    paddingRight: NETFLIX_SPACING.xl,
  },
  badgesContainer: {
    flexDirection: 'row',
    gap: NETFLIX_SPACING.md,
    alignItems: 'center',
    marginBottom: NETFLIX_SPACING.md,
  },
  badge: {
    backgroundColor: NETFLIX_COLORS.primary,
    paddingHorizontal: NETFLIX_SPACING.md,
    paddingVertical: 6,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  comingSoonBadge: {
    backgroundColor: '#FF6B6B',
    paddingHorizontal: NETFLIX_SPACING.md,
    paddingVertical: 6,
    borderRadius: 4,
  },
  comingSoonBadgeText: {
    color: NETFLIX_COLORS.white,
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 1,
  },
  badgeText: {
    color: NETFLIX_COLORS.white,
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 1,
  },
  title: {
    fontSize: 42,
    fontWeight: '700',
    color: NETFLIX_COLORS.white,
    marginBottom: NETFLIX_SPACING.md,
    lineHeight: 48,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: NETFLIX_SPACING.lg,
    marginBottom: NETFLIX_SPACING.lg,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: NETFLIX_SPACING.sm,
  },
  rating: {
    color: NETFLIX_COLORS.accent,
    fontSize: 13,
    fontWeight: '600',
  },
  year: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 13,
  },
  duration: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 13,
  },
  description: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 13,
    lineHeight: 20,
    marginBottom: NETFLIX_SPACING.lg,
  },
  buttons: {
    flexDirection: 'row',
    gap: NETFLIX_SPACING.md,
    marginBottom: NETFLIX_SPACING.xl,
  },
  playBtn: {
    backgroundColor: NETFLIX_COLORS.primary,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: 12,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: NETFLIX_SPACING.sm,
  },
  playBtnText: {
    color: NETFLIX_COLORS.white,
    fontSize: 13,
    fontWeight: '700',
  },
  addBtn: {
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.primary,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: 12,
    borderRadius: 4,
  },
  addBtnText: {
    color: NETFLIX_COLORS.primary,
    fontSize: 13,
    fontWeight: '700',
  },
  subscribeBtn: {
    backgroundColor: NETFLIX_COLORS.primary,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: 12,
    borderRadius: 4,
    marginTop: NETFLIX_SPACING.md,
    alignSelf: 'flex-start',
  },
  subscribeBtnText: {
    color: NETFLIX_COLORS.white,
    fontSize: 13,
    fontWeight: '700',
  },
  imageContainer: {
    width: 280,
    height: 400,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: NETFLIX_COLORS.surface,
  },
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
});
